import { Alert } from 'antd';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useParams } from 'react-router-dom';

import { useGetRunningTasksQuery } from '../../api/variantApi';
import { RUNNING_TASK_POLLING_INTERVAL } from '../../running_tasks/constants';
import { useHasRunningRefresh } from '../hooks/useHasRunningRefresh';
import { useVariantLock } from '../hooks/useVariantLock';
import { clearVariantLock, selectVariantLock } from '../redux';

/**
 * Route-level wrapper for /variant-workflow/:vid. Mounted once per variant
 * ID, so it owns the variant lock heartbeat (see useVariantLock.ts for why
 * there is no explicit release on unmount - the lock simply expires via the
 * backend TTL once the heartbeat stops).
 *
 * Data fetching lives in the route loader (Startup.tsx): "Route loaders
 * provide data to route components before they are rendered." This wrapper
 * only renders the active tab via <Outlet />.
 * Quelle: https://reactrouter.com/start/data/route-object#loader
 * Quelle: https://reactrouter.com/api/components/Outlet
 */
const VariantWorkflowWrapper = () => {
  const { vid } = useParams<{ vid: string }>();
  const dispatch = useDispatch();
  const lock = useSelector(selectVariantLock);
  const hasRunningRefresh = useHasRunningRefresh(vid);
  // The running task shows the banner right after the user confirms the
  // refresh; the backend lock covers refreshes started by other users/tabs.
  const isRefreshLocked = hasRunningRefresh || lock?.lock_type === 'refresh';
  const { data: runningTasks = [], isSuccess: runningTasksLoaded } =
    useGetRunningTasksQuery(undefined, {
      pollingInterval: RUNNING_TASK_POLLING_INTERVAL,
    });

  // Lock heartbeat is scoped to this wrapper's lifecycle. When the user
  // navigates to a different variant the wrapper unmounts, the heartbeat
  // stops, and the previous lock expires via the backend TTL.
  useVariantLock(vid);

  React.useEffect(() => {
    if (
      runningTasksLoaded &&
      lock?.lock_type === 'refresh' &&
      (!lock.running_task_id ||
        !runningTasks.some(
          (task) =>
            task.task_id === lock.running_task_id && task.status === 'running',
        ))
    ) {
      dispatch(clearVariantLock());
    }
  }, [dispatch, lock, runningTasks, runningTasksLoaded]);

  return (
    <>
      {isRefreshLocked && (
        <Alert
          className="variant-workflow__refresh-lock-alert"
          message="Variant locked during refresh"
          description="This variant is read-only while refresh and recalculation are running."
          type="warning"
          showIcon
        />
      )}
      <Outlet />
    </>
  );
};

export default VariantWorkflowWrapper;
